<script>
import { useAdmin } from 'dashboard/composables/useAdmin';
import BackButton from '../../../../components/widgets/BackButton.vue';
import SearchInput from './SearchInput.vue';
import PostsFilter from './PostsFilter.vue';
import { ref } from 'vue';
import CustomIcon from 'dashboard/components/ui/CustomIcons.vue';
import Icon from 'next/icon/Icon.vue';
import wootConstants from 'dashboard/constants/globals';

export default {
  name: 'PostsHeader',
  components: {
    BackButton,
    SearchInput,
    PostsFilter,
    CustomIcon,
    Icon,
  },
  props: {
    tabs: {
      type: Array,
      default: () => [],
      validator: value => {
        return value.every(tab => tab.name);
      },
    },
    headerTitle: {
      default: '',
      type: String,
    },
    buttonRoute: {
      default: '',
      type: String,
    },
    buttonText: {
      default: '',
      type: String,
    },
    icon: {
      default: '',
      type: String,
    },
    showBackButton: { type: Boolean, default: false },
    showNewButton: { type: Boolean, default: false },
    backUrl: {
      type: [String, Object],
      default: '',
    },
    backButtonLabel: {
      type: String,
      default: '',
    },
    showSidemenuIcon: {
      type: Boolean,
      default: true,
    },
    initialTab: {
      type: String,
      default: '',
    },
    initialFilter: {
      type: Object,
      default: () => ({
        actives: 'all',
        sort: wootConstants.POSTS_SORT_BY.NEWEST,
      }),
    },
    initialSearchWord: {
      type: String,
      default: '',
    },
    listLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['tabChange', 'filterChange', 'search', 'refresh'],
  setup() {
    const { isAdmin } = useAdmin();
    const searchQuery = ref('');

    return {
      isAdmin,
      searchQuery,
    };
  },
  data() {
    return {
      showPostsFilterDisplayMenu: false,
      filter: {},
      selectedTab: '',
      filterButtonRect: null,
      debounce: null,
    };
  },
  computed: {
    iconClass() {
      return `icon ${this.icon} header--icon`;
    },
    selectedTabIndex() {
      return this.tabs.findIndex(tab => tab.name === this.selectedTab);
    },
    filterDropdownStyle() {
      if (!this.filterButtonRect) return {};
      const isRTL = document.dir === 'rtl';
      return {
        top: `${this.filterButtonRect.bottom + 8}px`,
        [isRTL ? 'left' : 'right']:
          `${window.innerWidth - this.filterButtonRect.right}px`,
      };
    },
  },
  watch: {
    initialTab: {
      handler(newTab) {
        this.selectedTab = newTab;
      },
      immediate: true,
    },
    initialFilter: {
      handler(filter) {
        this.selectedFilter = filter;
        this.filter = {
          ...this.filter,
          ...this.selectedFilter,
        };
      },
      immediate: true,
    },
    initialSearchWord: {
      handler(newSearchWord) {
        this.searchQuery = newSearchWord;
      },
      immediate: true,
    },
  },
  methods: {
    handleTabChange(tabName) {
      this.$emit('tabChange', tabName);
    },
    onClickTabChange(index) {
      const tab = this.tabs?.[index];
      if (tab?.name) {
        this.handleTabChange(tab.name);
      }
    },
    openPostsFilterDisplayMenu() {
      this.showPostsFilterDisplayMenu = !this.showPostsFilterDisplayMenu;
      if (this.showPostsFilterDisplayMenu) {
        this.$nextTick(() => {
          const button = this.$refs.filterButton;
          if (button) {
            this.filterButtonRect = button.getBoundingClientRect();
          }
        });
      }
    },
    onFilterChange(option) {
      const postFilter = {
        sort: option.sort,
        actives: option.actives,
        publicationDateStart: option.publicationDateStart,
        publicationDateEnd: option.publicationDateEnd,
      };
      this.filter = postFilter;
      this.$emit('filterChange', postFilter);
    },
    debounceSearch(e) {
      this.searchQuery = e.target.value;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(async () => {
        if (this.searchQuery.length > 2 || this.searchQuery.match(/^[0-9]+$/)) {
          this.$emit('search', this.searchQuery);
        } else {
          this.$emit('search', '');
        }
      }, 500);
    },
    emitRefresh() {
      if (this.listLoading) return;
      this.$emit('refresh');
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col px-4 pt-4 pb-0 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800/50"
  >
    <!-- Search Bar -->
    <div class="mb-6">
      <SearchInput
        search-placeholder="Pesquisar..."
        :search-term="searchQuery"
        @input="debounceSearch"
      />
    </div>

    <!-- Header with title and actions -->
    <div class="flex justify-between items-center mb-6">
      <h1
        class="flex items-center mb-0 text-2xl font-semibold text-slate-900 dark:text-slate-100"
      >
        <woot-sidemenu-icon v-if="showSidemenuIcon" />
        <BackButton
          v-if="showBackButton"
          :button-label="backButtonLabel"
          :back-url="backUrl"
          class="ml-2 mr-4"
        />
        <fluent-icon
          v-if="icon"
          :icon="icon"
          :class="iconClass"
          class="hidden ml-1 mr-2 rtl:ml-2 rtl:mr-1 md:block"
        />
        <slot />
        <span class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          {{ headerTitle }}
        </span>
      </h1>
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="group relative flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 ease-in-out shadow-sm hover:shadow-md border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:hover:bg-slate-100 dark:disabled:hover:bg-slate-800 disabled:hover:shadow-sm"
          :disabled="listLoading"
          :aria-label="$t('COMMENTS.MODERATION.REFRESH_ARIA')"
          :title="$t('COMMENTS.MODERATION.REFRESH_ARIA')"
          @click="emitRefresh"
        >
          <Icon
            icon="i-lucide-refresh-cw"
            class="size-4 text-slate-600 dark:text-slate-400 shrink-0 motion-safe:transition-transform"
            :class="{ 'motion-safe:animate-spin': listLoading }"
          />
          <span class="text-sm font-medium">
            {{ $t('COMMENTS.MODERATION.REFRESH') }}
          </span>
        </button>
        <!-- Modern Filter Button -->
        <div class="relative">
          <button
            ref="filterButton"
            class="group flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 ease-in-out shadow-sm hover:shadow-md border border-slate-200 dark:border-slate-700"
            :class="{
              'ring-2 ring-woot-500 ring-offset-1 dark:ring-offset-slate-900 bg-slate-200 dark:bg-slate-700':
                showPostsFilterDisplayMenu,
            }"
            type="button"
            @click="openPostsFilterDisplayMenu"
          >
            <CustomIcon
              group="general"
              name="FILTER"
              :color="showPostsFilterDisplayMenu ? '#667085' : '#5A6169'"
            />
            <span class="text-sm font-medium">{{
              $t('COMMENTS.MODERATION.FILTERS')
            }}</span>
            <fluent-icon
              icon="chevron-down"
              size="16"
              class="text-slate-500 dark:text-slate-400 transition-transform duration-200"
              :class="{ 'rotate-180': showPostsFilterDisplayMenu }"
            />
          </button>
          <div
            v-if="showPostsFilterDisplayMenu"
            v-on-clickaway="openPostsFilterDisplayMenu"
            class="fixed inset-0 z-[100] bg-black/5 dark:bg-black/20 backdrop-blur-[1px]"
            @click="openPostsFilterDisplayMenu"
          >
            <div
              :style="filterDropdownStyle"
              class="fixed animate-slide-down"
              @click.stop
            >
              <PostsFilter :initial-state="filter" @filter="onFilterChange" />
            </div>
          </div>
        </div>

        <!-- New Button -->
        <router-link
          v-if="showNewButton && isAdmin"
          :to="buttonRoute"
          class="button success button--fixed-top px-4 py-2 rounded-lg flex gap-2 items-center shadow-sm hover:shadow-md transition-all duration-200"
        >
          <fluent-icon icon="add-circle" size="16" />
          <span class="button__content text-sm font-medium">
            {{ buttonText }}
          </span>
        </router-link>
      </div>
    </div>

    <!-- Tabs Section -->
    <woot-tabs
      v-if="tabs.length > 0"
      class="font-semibold [&_.tabs]:p-0 remove-border"
      :index="selectedTabIndex"
      @change="onClickTabChange"
    >
      <woot-tabs-item
        v-for="(tab, index) in tabs"
        :key="tab.key"
        :index="index"
        :name="tab.name"
        :count="tab?.count ?? 0"
        show-badge
        @click="handleTabChange(tab.name)"
      />
    </woot-tabs>
  </div>
</template>

<style scoped>
.remove-border {
  border-bottom-width: 0;
}

@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-down {
  animation: slide-down 0.2s ease-out;
}
</style>
